import { getCroppedImg, getCroppedSVG } from "./helper";
import { Dialog, DialogContent, DialogTitle } from "../dialog";
import { Button } from "../button";
import { XIcon } from "lucide-react";
import { useCallback, useState } from "react";
import Cropper, { Area } from "react-easy-crop";
import { CropperModalProps } from "./type";

export const CropperModal = ({
  open,
  onOpenChange,
  imageSrc,
  isSVG,
  onConfirm,
  onCancel,
  onError,
  cropSize,
  outputExactCropSize,
  targetFileSizeKB,
  generateBlobUrlOutput,
  title,
  props
}: CropperModalProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState<number | undefined>();
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  const handleClose = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const handleCancel = useCallback(() => {
    if (onCancel) {
      onCancel();
    }
    handleClose();
  }, [handleClose, onCancel]);

  const handleCropComplete = useCallback((_: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleConfirm = async () => {
    if (!imageSrc || !onConfirm) {
      handleClose();
      return;
    }
    if (!generateBlobUrlOutput) {
      onConfirm({ crop, croppedAreaPixels });
      return;
    }
    if (!croppedAreaPixels) {
      // return original image
      onConfirm({ crop, croppedAreaPixels, croppedImageBlobUrl: imageSrc });
      return;
    }
    setIsLoading(true);
    try {
      const desiredDimension = outputExactCropSize ? cropSize : undefined;
      const croppedImage =
        isSVG ?
          await getCroppedSVG(imageSrc, croppedAreaPixels, desiredDimension, targetFileSizeKB)
        : await getCroppedImg(imageSrc, croppedAreaPixels, desiredDimension, targetFileSizeKB);
      onConfirm({ crop, croppedAreaPixels, croppedImageBlobUrl: croppedImage });
    } catch (e: unknown) {
      if (onError) {
        onError(e);
      }
    } finally {
      setIsLoading(false);
      handleClose();
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          handleClose();
        } else {
          onOpenChange(true);
        }
      }}
    >
      <DialogContent
        className="flex flex-col w-fit !max-w-6xl p-0 overflow-clip gap-0 border-0"
        {...props?.dialogContent}
      >
        <div
          className="flex w-full bg-[#82B495] text-white justify-between items-center p-4"
          {...props?.dialogTitleContainer}
        >
          <DialogTitle className="text-2xl font-semibold" {...props?.dialogTitle}>
            {title ?? "Crop Image"}
          </DialogTitle>
          <button
            type="button"
            className="text-white hover:text-gray-100 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleCancel}
            disabled={isLoading}
          >
            <XIcon className="w-4 h-4" />
          </button>
        </div>
        <div className="w-120 md:w-[50vw] h-[70vh] mx-auto" {...props?.cropperContainer}>
          <Cropper
            image={imageSrc ?? undefined}
            crop={crop}
            onCropChange={setCrop}
            zoom={zoom}
            onZoomChange={setZoom}
            style={{ containerStyle: { position: "relative", width: "100%", height: "100%" } }}
            onCropComplete={handleCropComplete}
            aspect={cropSize ? cropSize.width / cropSize.height : undefined}
            zoomWithScroll
            showGrid
            restrictPosition
            {...props?.cropper}
          />
        </div>

        {/* Footer */}
        <div className="w-full py-4 px-8 min-h-10">
          <div className="flex w-full justify-between">
            <Button type="button" variant="cancel" onClick={handleCancel} disabled={isLoading}>
              Cancel
            </Button>
            <div className="ml-auto flex gap-x-2">
              <Button type="button" variant="default" onClick={handleConfirm} disabled={isLoading}>
                Confirm
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CropperModal;
